import { CLASSES } from "@eldrun/content";
import { CHARACTER_SURFACE_RANGES, type CharacterSurfaceStyle } from "../render/character-surface/config.ts";
import { surfaceColor, writeSurfaceParams } from "./surface-params.ts";

/** Os controles por uniform (mudam na hora) e o passo de cada um. */
const SLIDERS = [
  { field: "glass", label: "Vidro", step: 0.05 },
  { field: "fresnel", label: "Fresnel", step: 0.05 },
] as const satisfies readonly { field: keyof typeof CHARACTER_SURFACE_RANGES; label: string; step: number }[];

/** Cores que o painel oferece: as classes e os papéis (a URL usa os mesmos nomes). */
const COLORS = [...CLASSES.map((c) => c.id), "chefe", "inimigo", "neutro"] as const;

export interface SurfacePanel {
  remove(): void;
}

/**
 * Painel de ajuste do visor (`&painel=1`, F120; sem lib de GUI): liga e desliga, cor, vidro e Fresnel
 * ao vivo (uniforms), cada ajuste gravado na URL na hora (a URL é a fonte de verdade do visor: cada
 * captura fica reproduzível). Fase 15: os controles das linhas e dos nós saíram com eles.
 */
export function createSurfacePanel(parent: HTMLElement, style: Readonly<CharacterSurfaceStyle>, apply: (style: Partial<CharacterSurfaceStyle>) => void): SurfacePanel {
  // Cada ajuste vai aos uniforms e à URL na hora (sem recarregar): o link leva tudo.
  const onChange = (change: Partial<CharacterSurfaceStyle>, colorName?: string) => {
    apply(change);
    const url = new URL(window.location.href);
    writeSurfaceParams(url.searchParams, change, colorName);
    window.history.replaceState(window.history.state, "", url);
  };
  const root = document.createElement("form");
  root.id = "painel-superficie";
  root.setAttribute("aria-label", "Superfície dos atores");
  Object.assign(root.style, {
    position: "fixed",
    right: "8px",
    top: "8px",
    display: "grid",
    gap: "6px",
    width: "240px",
    padding: "10px",
    font: "12px/1.3 ui-monospace, Menlo, monospace",
    color: "white",
    background: "rgba(14, 26, 20, 0.82)",
    borderRadius: "6px",
    zIndex: "11",
  });
  root.addEventListener("submit", (e) => e.preventDefault());

  const row = (label: string, input: HTMLElement, id: string) => {
    const l = document.createElement("label");
    l.htmlFor = id;
    l.textContent = label;
    l.style.display = "grid";
    l.style.gap = "2px";
    input.id = id;
    l.appendChild(input);
    root.appendChild(l);
  };

  const enabled = document.createElement("input");
  enabled.type = "checkbox";
  enabled.checked = style.enabled;
  enabled.addEventListener("change", () => onChange({ enabled: enabled.checked }));
  row("Superfície ligada", enabled, "superficie-ligada");

  const color = document.createElement("select");
  for (const name of COLORS) {
    const option = document.createElement("option");
    option.value = name;
    option.textContent = name;
    option.selected = surfaceColor(name) === style.accentColor;
    color.appendChild(option);
  }
  color.addEventListener("change", () => {
    const hex = surfaceColor(color.value);
    if (hex) onChange({ accentColor: hex }, color.value);
  });
  // A cor do acento é também a da máscara da roupa (a cor da classe, no shader).
  row("Cor (acento e máscara da roupa)", color, "superficie-cor");

  for (const s of SLIDERS) {
    const [min, max] = CHARACTER_SURFACE_RANGES[s.field];
    const input = document.createElement("input");
    input.type = "range";
    input.min = String(min);
    input.max = String(max);
    input.step = String(s.step);
    input.value = String(style[s.field]);
    const value = document.createElement("output");
    value.htmlFor.add(`superficie-${s.field}`);
    // O valor de verdade (o da URL pode cair entre os passos do controle).
    value.textContent = String(style[s.field]);
    input.addEventListener("input", () => {
      value.textContent = input.value;
      onChange({ [s.field]: Number(input.value) });
    });
    row(s.label, input, `superficie-${s.field}`);
    root.appendChild(value);
  }

  parent.appendChild(root);
  return { remove: () => root.remove() };
}
